// Utilities for RequirementCards summary text construction
import { Guideline } from '../../types/profileTypes';

type SummaryOptions = {
  targetName?: string; // For AvoidCrossCategoryComparisons - resolved display name
};

export function getSummaryText(guideline: Guideline, opts: SummaryOptions = {}): string {
  switch (guideline.type) {
    case 'ApprovedQualifierPhrasesBeforeClaims': {
      const { approvedPhrase, claim } = guideline.parameters as any;
      return `Use "${approvedPhrase}" qualifier immediately before the following claim: "${claim}."`;
    }
    case 'ApprovedSubstitutions': {
      const { approvedTerm, restrictedTerm } = guideline.parameters as any;
      return buildApprovedSubstitutionsSummary(approvedTerm, restrictedTerm);
    }
    case 'AvoidRestrictedTextForClaims': {
      const { topic, referenceText } = guideline.parameters as any;
      return buildAvoidRestrictedTextForClaimsSummary(topic, referenceText);
    }
    case 'ApprovedNutrientRelatedPhrases': {
      const { nutrient, amount } = guideline.parameters as any;
      return buildApprovedNutrientRelatedPhrasesSummary(nutrient, amount);
    }
    case 'AvoidSpecificClaimLanguage': {
      const { claimType, topic } = guideline.parameters as any;
      return buildAvoidSpecificClaimLanguageSummary(claimType, topic);
    }
    case 'ApprovedOnPackageLanguage': {
      const { onPackageText } = guideline.parameters as any;
      return buildApprovedOnPackageLanguageSummary(onPackageText);
    }
    case 'ApprovedProductAttributeAndDisclaimerText': {
      const { productAttribute, approvedReplacement, restrictedText, disclaimer } = guideline.parameters as any;
      return buildApprovedProductAttributeAndDisclaimerSummary(
        productAttribute,
        approvedReplacement,
        restrictedText,
        disclaimer
      );
    }
    case 'BrandNameFormatting': {
      const { brandName, condition, format, customText } = guideline.parameters as any;
      return buildBrandNameFormattingSummary(brandName, condition, format, customText);
    }
    case 'AvoidCrossCategoryComparisons': {
      const { comparisonType, target } = guideline.parameters as any;
      const resolved = opts.targetName || target;
      return buildAvoidCrossCategoryComparisonsSummary(comparisonType, resolved);
    }
    default:
      return '';
  }
}

export function buildApprovedSubstitutionsSummary(approvedTerm: string, restrictedTerm: string): string {
  return `When writing new content, use "${approvedTerm}" instead of "${restrictedTerm}."`;
}

export function buildAvoidRestrictedTextForClaimsSummary(topic: string, referenceText: string): string {
  return `Avoid making claims about "${topic}" that reference "${referenceText}."`;
}

export function buildApprovedNutrientRelatedPhrasesSummary(nutrient: string, amount: string): string {
  return `When mentioning ${nutrient} always use the following statement: “${amount} Grams (g) of ${nutrient} per serving.”`;
}

export function buildAvoidSpecificClaimLanguageSummary(claimType: string, topic: string): string {
  return `Avoid claims ${claimType}: "${topic}."`;
}

export function buildApprovedOnPackageLanguageSummary(onPackageText: string): string {
  return `Only use the on-package language: “${onPackageText}."`;
}

export function buildApprovedProductAttributeAndDisclaimerSummary(
  productAttribute: string,
  approvedReplacement: string,
  restrictedText: string,
  disclaimer: string
): string {
  return `When referring to “${productAttribute},” use “${approvedReplacement}” instead of “${restrictedText}.” Disclaimer: “${disclaimer}.”`;
}

export function buildBrandNameFormattingSummary(
  brandName: string,
  condition: 'always' | 'never',
  format: string,
  customText?: string
): string {
  const conditionText = condition === 'always' ? 'should always' : 'should never';
  const formatText = format === 'in custom text' && customText ? `"${customText}"` : format;
  return `${brandName} ${conditionText} be written ${formatText}.`;
}

export function buildAvoidCrossCategoryComparisonsSummary(comparisonType: 'category' | 'sub-category', targetName: string): string {
  return `Avoid making product comparison claims against products in the ${comparisonType}: "${targetName}."`;
}


